import { devModeMocks } from "@api/core-api-utils";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import {
  BaseCompanyTeamIdProps,
  BaseTeamIdProps,
  TeamId,
} from "@custom-types/base-types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { RootState } from "@store/store-helper";
import { teamAdapter } from "@store/teams/teams-slice";
import {
  AddMembersToTeamProps,
  AddMembersToTeamResult,
  AddTeamProps,
  FetchTeamMembersResponse,
  RemoveTeamMembers,
  SearchedTeamsProps,
} from "@store/teams/teams-slice-types";

/**
 * Fetches teams from the backend so they can be put into the store
 * @throws {HttpUtils.FetchError} if any Core API client request fails
 */
export const fetchTeams = createAsyncThunk<
  FaroCoreApiTypes.GetTeamsResponse,
  SearchedTeamsProps,
  { state: RootState }
>(
  "teams/fetchTeams",
  async ({ searchText }, { getState }) => {
    const {
      sdbCompany: { selectedSdbCompanyId },
      app: { isDevModeEnabled },
    } = getState();

    if (!selectedSdbCompanyId) {
      throw new Error("No companyId was given to search for teams");
    }

    try {
      return await faroCoreApiClient.teams.getTeams({
        companyId: selectedSdbCompanyId,
        payload: { search: searchText },
      });
    } catch (error) {
      if (isDevModeEnabled) {
        return devModeMocks.teams;
      }
      throw error;
    }
  },
  {
    condition: ({ searchText }, { getState }) => {
      const state = getState();

      // If succeeded fetching status means the data is already fetched once and no need to do it again if there is no search text
      // and there is no search text, do not fetch again
      if (state.teams.fetching.status === "succeeded" && !searchText) {
        return false;
      }

      return true;
    },
  }
);

/**
 * Fetches the details of a team
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchTeamDetails = createAsyncThunk<
  FaroCoreApiTypes.Team,
  BaseTeamIdProps,
  { state: RootState }
>("teams/fetchTeamDetails", async ({ teamId }, { getState }) => {
  const {
    sdbCompany: { selectedSdbCompanyId },
  } = getState();

  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetch team details");
  }

  return await faroCoreApiClient.teams.fetchTeamById({
    companyId: selectedSdbCompanyId,
    teamId,
  });
});

/**
 * Fetches the details of a team as member
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchTeamAsMember = createAsyncThunk<
  FaroCoreApiTypes.CompanyMemberDetails,
  BaseTeamIdProps,
  { state: RootState }
>("teams/fetchTeamAsMember", async ({ teamId }, { getState }) => {
  const {
    sdbCompany: { selectedSdbCompanyId },
  } = getState();

  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetch the team");
  }

  /**
   * Fetches the details of a team as member. This is the way to get all the groups and projects that the team is part of.
   * Backend will accept the team ID in fetching members route and return it in the member type
   */
  return await faroCoreApiClient.company.getMemberDetailsInCompany({
    companyId: selectedSdbCompanyId,
    identity: teamId,
  });
});

/**
 * Creates a team in the backend and adds it to the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const createTeam = createAsyncThunk<FaroCoreApiTypes.Team, AddTeamProps>(
  "teams/createTeam",
  async ({ companyId, teamName, description }) => {
    const team = await faroCoreApiClient.teams.createNewTeam({
      companyId,
      payload: {
        name: teamName,
        description,
      },
    });

    return team;
  }
);

/**
 * Fetches team members from the backend so they can be put into the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchTeamMembers = createAsyncThunk<
  FetchTeamMembersResponse,
  FaroCoreApiTypes.FetchTeamMembersProps,
  { state: RootState }
>(
  "teams/fetchTeamMembers",
  async ({ companyId, teamId, start }) => {
    const result = await faroCoreApiClient.teams.fetchTeamMembers({
      companyId,
      teamId,
      start,
    });

    return { teamId, members: result.data, next: result.next };
  },
  {
    condition: ({ start }, api) => {
      const state = api.getState();

      // Skip fetching if we already have the data and not fetching again
      if (
        state.teams.fetching.isFetchingTeamMembers === "succeeded" &&
        !start
      ) {
        return false;
      }

      return true;
    },
  }
);

/**
 * Invite members to a team
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const addMembersToTeam = createAsyncThunk<
  AddMembersToTeamResult,
  AddMembersToTeamProps
>("teams/addMembersToTeam", async ({ companyId, teamId, members }) => {
  const addMembersResponse = await faroCoreApiClient.teams.addMembersToTeam({
    companyId,
    teamId,
    payload: { members },
  });

  return { ...addMembersResponse, teamId };
});

interface UpdateTeamDetailsProps extends BaseTeamIdProps {
  /** The payload for updating team details */
  payload: FaroCoreApiTypes.UpdateTeamPayload;
}

/**
 * Update team details
 * @throws {Error} if there is no selected company ID or team ID or if team is not found in the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateTeamDetails = createAsyncThunk<
  FaroCoreApiTypes.Team,
  UpdateTeamDetailsProps,
  {
    state: RootState;
  }
>("teams/updateTeamDetails", async ({ teamId, payload }, { getState }) => {
  const { teams, sdbCompany } = getState();

  /**
   * The ID of the team that is going to be updated.
   * If teamId is not provided, the selectedTeamId from store is selected
   */
  const updatingTeamId = teamId || teams.selectedTeam.id;

  if (!sdbCompany.selectedSdbCompanyId) {
    throw new Error("No companyId exist to updateTeamDetails");
  }

  if (!updatingTeamId) {
    throw new Error("No teamId exist to updateTeamDetails");
  }

  const team = teamAdapter.getSelectors().selectById(teams, updatingTeamId);
  if (!team) {
    throw new Error("Team not found");
  }

  const updatedTeam = await faroCoreApiClient.teams.updateTeam({
    companyId: sdbCompany.selectedSdbCompanyId,
    teamId: updatingTeamId,
    payload,
  });

  return updatedTeam;
});

/**
 * Remove a member from the team using the backend,
 * and then removes it from team in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const removeMembersFromTeam = createAsyncThunk<
  RemoveTeamMembers,
  RemoveTeamMembers
>("teams/removeMembersFromTeam", async ({ companyId, teamId, memberIds }) => {
  await faroCoreApiClient.teams.removeMembersFromTeam({
    companyId,
    teamId,
    payload: {
      members: memberIds,
    },
  });

  return {
    teamId,
    companyId,
    memberIds,
  };
});

/**
 * Delete a team
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const deleteTeam = createAsyncThunk<TeamId, BaseCompanyTeamIdProps>(
  "teams/deleteTeam",
  async ({ companyId, teamId }) => {
    await faroCoreApiClient.teams.deleteTeam({ companyId, teamId });
    return teamId;
  }
);
